var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef } from 'react';
import clsx from 'clsx';
import isNaN from 'lodash.isnan';
import { useClasses, useLayoutProps, useProps } from '@hawkins/helpers';
import { CircularProgress } from './circularProgress';
import { LinearProgress } from './linearProgress';
import { useLoaderStyles } from './loader.styles';
var displayName = 'Loader';
/**
 * The `<Loader>` component will provide context to the progressive state of an action or page.
 *
 * There are two types of `<Loader>`s available: `circular` and `linear`
 *
 * Within the two types, there are also two different states: `determinate` and `indeterminate`
 *
 * When the system is unable to determine the amount of time the process will take, use an indeterminate loader.
 * When the system is able to understand how long a process will take, use a determinate loader.
 */
export var Loader = forwardRef(function (props, ref) {
    var _a;
    // Props
    var _b = useProps(displayName, props), classesProp = _b.classes, className = _b.className, _c = _b.component, Component = _c === void 0 ? 'span' : _c, layout = _b.layout, _d = _b.size, size = _d === void 0 ? 32 : _d, _e = _b.state, state = _e === void 0 ? 'indeterminate' : _e, styleProp = _b.style, _f = _b.thickness, thickness = _f === void 0 ? 2.5 : _f, value = _b.value, _g = _b.variant, variant = _g === void 0 ? 'circular' : _g, restProps = __rest(_b, ["classes", "className", "component", "layout", "size", "state", "style", "thickness", "value", "variant"]);
    // Styling
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var baseClasses = useLoaderStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var loaderClassName = clsx(classes.HAWKINS_BASE, classes.root, className, (_a = {
            'Hawkins-circular-loader': variant === 'circular'
        },
        _a[classes.circular] = variant === 'circular',
        _a[classes.circularIndeterminate] = variant === 'circular' && state === 'indeterminate',
        _a[classes.linear] = variant !== 'circular',
        _a[classes.linearDeterminate] = variant !== 'circular' && state === 'determinate',
        _a[classes.linearIndeterminate] = variant !== 'circular' && state !== 'determinate',
        _a));
    var checkedValue = isNaN(value) ? 0 : value;
    if (variant === 'circular' && state === 'indeterminate') {
        return (_jsx("div", __assign({ className: loaderClassName, style: __assign({ height: size, width: size }, style), ref: ref }, restProps)));
    }
    if (variant === 'circular') {
        return (_jsx(CircularProgress, __assign({ className: loaderClassName, component: Component, ref: ref, size: size, style: style, thickness: thickness, value: checkedValue }, restProps)));
    }
    return (_jsx(LinearProgress, __assign({ className: loaderClassName, indeterminateProgressBarClassName: classes.linearIndeterminateProgressBar, indeterminateProgressTrackClassName: classes.linearIndeterminateProgressTrack, ref: ref, state: state, style: style, value: checkedValue }, restProps)));
});
Loader.displayName = displayName;
